import { useCallback, useEffect, useRef, useState } from 'react';

import {
  buildImageNodes,
  buildVideoNodes,
  CANVAS_ASSET_DROP_TYPE,
  CANVAS_IMAGE_NODE_SPACING,
  type CanvasAssetPayload,
  commands,
  DEFAULT_CANVAS_IMAGE_HEIGHT,
  DEFAULT_CANVAS_IMAGE_WIDTH,
  type ImageNodesLayout,
  payloadToImageNodeSources,
  processAssetDropPayload,
  useCommandHistory,
  useEditCanvasStore,
} from '@air/feature-edit-canvas';
import { useToasts } from '@air/provider-toast';

import { useWorkspaceRightPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelStore';
import { useCanConsumeMoreCredits } from '~/hooks/useCanConsumeMoreCredits';

type UseCanvasAssetDropTargetParams = {
  canvasId: string;
  canEditCanvas: boolean;
  onSelectCanvas: (canvasId: string) => void;
};

const MAX_ATTEMPTS = 20;
const RETRY_MS = 100;

export const useCanvasAssetDropTarget = ({
  canvasId,
  canEditCanvas,
  onSelectCanvas,
}: UseCanvasAssetDropTargetParams) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);
  const { showToast } = useToasts();
  const { canConsumeMoreCredits, showCreditsDepletedModal } = useCanConsumeMoreCredits();
  const execute = useCommandHistory((state) => state.execute);

  const insertDroppedAssets = useCallback(
    (imageAssets: ReturnType<typeof payloadToImageNodeSources>, videoAssets: CanvasAssetPayload[]) => {
      let attempts = 0;

      const tryInsert = () => {
        const rightPanelState = useWorkspaceRightPanelStore.getState();
        const canvasStoreState = useEditCanvasStore.getState();
        const isTargetCanvasOpen = rightPanelState.open && rightPanelState.selectedCanvasId === canvasId;
        const hasCanvasSyncCallbacks = !!canvasStoreState.createNodes;

        if (!isTargetCanvasOpen || !hasCanvasSyncCallbacks) {
          attempts += 1;
          if (attempts <= MAX_ATTEMPTS) {
            setTimeout(tryInsert, RETRY_MS);
          }

          return;
        }

        requestAnimationFrame(() => {
          const existingNodes = useEditCanvasStore.getState().nodes;
          const startX =
            existingNodes.length > 0
              ? existingNodes.reduce((max, node) => {
                  const width = typeof node.style?.width === 'number' ? node.style.width : DEFAULT_CANVAS_IMAGE_WIDTH;

                  return Math.max(max, node.position.x + width);
                }, 0)
              : 0;
          const startY = existingNodes[0]?.position.y ?? 0;

          const sources = imageAssets.map((source) => ({
            ...source,
            width: source.width ?? DEFAULT_CANVAS_IMAGE_WIDTH,
            height: source.height ?? DEFAULT_CANVAS_IMAGE_HEIGHT,
          }));

          const layout: ImageNodesLayout = {
            kind: 'row',
            startX,
            startY,
            spacing: CANVAS_IMAGE_NODE_SPACING,
          };

          const { nodes: imageNodes, nextOffsetX: imageEndOffsetX } = buildImageNodes(sources, layout);
          const { nodes: videoNodes } = buildVideoNodes(videoAssets, layout, imageEndOffsetX);
          const nodesToAdd = [...imageNodes, ...videoNodes];

          if (nodesToAdd.length > 0) {
            execute(commands.addNodes(nodesToAdd, { zoomToFit: false, zoomToFitIfFirstNode: true }));
          }
        });
      };

      tryInsert();
    },
    [canvasId, execute],
  );

  const handleDrop = useCallback(
    (event: DragEvent) => {
      if (!canEditCanvas) {
        return;
      }

      if (!canConsumeMoreCredits) {
        showCreditsDepletedModal({ triggeredBy: 'canvas' });
        return;
      }

      const rawPayload = event.dataTransfer?.getData(CANVAS_ASSET_DROP_TYPE);

      if (!rawPayload) {
        return;
      }

      const result = processAssetDropPayload(rawPayload, 'canvas', showToast);

      if (!result || (result.imageAssets.length === 0 && result.videoAssets.length === 0)) {
        return;
      }

      onSelectCanvas(canvasId);
      const imageAssets = payloadToImageNodeSources(result.imageAssets);
      insertDroppedAssets(imageAssets, result.videoAssets);
    },
    [
      canConsumeMoreCredits,
      canEditCanvas,
      canvasId,
      insertDroppedAssets,
      onSelectCanvas,
      showCreditsDepletedModal,
      showToast,
    ],
  );

  const isOverItem = useCallback((event: DragEvent | { clientX: number; clientY: number }) => {
    const element = itemRef.current;

    if (!element) {
      return false;
    }
    const rect = element.getBoundingClientRect();

    return (
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom
    );
  }, []);

  useEffect(() => {
    const onDragOverCapture = (event: DragEvent) => {
      const hasAssetDropType = event.dataTransfer?.types.includes(CANVAS_ASSET_DROP_TYPE);

      if (canEditCanvas && canConsumeMoreCredits && hasAssetDropType && isOverItem(event)) {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer) {
          event.dataTransfer.dropEffect = 'copy';
        }
        setIsDragOver(true);
        return;
      }
      setIsDragOver(false);
    };

    const onDragLeaveCapture = (event: DragEvent) => {
      if (!isOverItem(event)) {
        setIsDragOver(false);
      }
    };

    const onDropCapture = (event: DragEvent) => {
      const hasAssetDropType = event.dataTransfer?.types.includes(CANVAS_ASSET_DROP_TYPE);

      if (!canEditCanvas || !canConsumeMoreCredits || !hasAssetDropType || !isOverItem(event)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);
      handleDrop(event);
    };

    document.addEventListener('dragover', onDragOverCapture, true);
    document.addEventListener('dragleave', onDragLeaveCapture, true);
    document.addEventListener('drop', onDropCapture, true);

    return () => {
      document.removeEventListener('dragover', onDragOverCapture, true);
      document.removeEventListener('dragleave', onDragLeaveCapture, true);
      document.removeEventListener('drop', onDropCapture, true);
    };
  }, [canConsumeMoreCredits, canEditCanvas, handleDrop, isOverItem]);

  return { itemRef, isDragOver };
};
